import type { Meal, MealState } from '@sentra/contracts';
import { Check, Croissant, Soup, UtensilsCrossed } from 'lucide-react';
import type { ComponentProps, ReactNode } from 'react';

import { Button } from '@/design/components/button';
import { cn } from '@/design/utils';

/**
 * The one control that records a serving, and the one place that decides how a sitting looks.
 *
 * **It sits beside `features/direction-button.tsx` in shape and in reasoning.** One component draws
 * every meal button, so the three cannot drift into three treatments, and `shell.web.test.tsx`
 * censuses this file's own `data-meal` attribute to exactly this file, for the reason it censuses
 * the direction attribute to that one.
 *
 * Neither attribute is spelled with its equals sign anywhere in this comment, deliberately: both
 * censuses match the literal INCLUDING the `=`, so a note that spelled one out would put this file
 * in the other's list.
 *
 * **No new colour, and NO colour at all, which is stronger than the design this started from.**
 * `design/tokens.css` reserves red, amber and green for refused, incomplete and accepted, and lets
 * green and red carry exactly one second meaning: a movement's DIRECTION. Three more hues would be
 * three more meanings in a palette whose restraint is what makes a full-screen red alert at a gate
 * readable. So a sitting is told apart by its ICON and its WORD, and the suggested one is marked the
 * way the expected direction is: a solid fill and more width.
 *
 * **The tick on a taken sitting is deliberately NOT green**, and that is a correction rather than a
 * preference. A `text-success` tick would sit on two backgrounds nobody has measured it against: the
 * accent fill, when the clock suggests a sitting somebody has already had, and `--control`, where
 * the sibling rule already records `text-danger` measuring 4.43:1 and failing. A state's ink may
 * only sit on that state's own background. The tick inherits the button's own foreground, which IS
 * measured for both variants, and what carries the meaning is the check MARK plus the time beneath
 * it — neither of which is a colour.
 *
 * **The emphasis is the fill and the width, never the hue.** An operator who cannot tell two tones
 * apart still reads which sitting the clock suggests, which is the property `DirectionButton` has
 * and this one had to keep.
 *
 * **A taken sitting stays pressable.** Disabling it would make a further helping unreachable at a
 * counter, and a further helping is a real thing that happens: a dropped tray, a meal logged against
 * the wrong person. Pressing it opens the reason dialog rather than submitting, so a misclick writes
 * nothing.
 */

/** The icon each sitting carries, so no call site chooses one. */
const MEAL_ICON = {
  breakfast: Croissant,
  lunch: UtensilsCrossed,
  dinner: Soup,
} as const satisfies Record<Meal, typeof Croissant>;

export function MealButton({
  meal,
  state,
  suggested,
  label,
  servedLabel,
  // Defaulted rather than left to the call site, exactly as `DirectionButton` defaults it: the
  // design-system `Button` sets no default type, so an untyped one inside a `<form>` is a SUBMIT.
  type = 'button',
  ...rest
}: Readonly<{
  meal: Meal;
  /** What this person has already had today, from the scan that identified them. */
  state: MealState;
  /** Whether the clock suggests this sitting. Drawn solid, and wider. */
  suggested: boolean;
  /** The already-translated word. A string rather than a key, exactly as the direction pair takes. */
  label: string;
  /** The already-translated "served at HH:MM" line, or `null` when it has not been. */
  servedLabel: string | null;
}> &
  Omit<ComponentProps<typeof Button>, 'variant' | 'className' | 'children'>): ReactNode {
  const Icon = MEAL_ICON[meal];

  return (
    <Button
      type={type}
      // The sitting as a closed-set token: the label is prose in two languages, and every
      // end-to-end flow that serves a meal finds the button by this.
      data-meal={meal}
      // Whether the allowance has gone, for a test and for a baseline, so neither has to read a
      // translated sentence to know which state it is looking at.
      data-served={state.served}
      variant={suggested ? 'default' : 'outline'}
      className={cn('h-auto flex-col items-start gap-0.5 py-2', suggested && 'min-w-48')}
      {...rest}
    >
      <span className="flex items-center gap-2">
        <Icon aria-hidden />
        {label}
        {state.served && <Check aria-hidden />}
      </span>
      {/*
        No opacity and no second colour: an alpha has one contrast ratio per backdrop rather than
        one ratio, which is the rule every hover in this product was rewritten to obey. The line
        inherits the variant's own measured foreground and is distinguished by SIZE.
      */}
      {servedLabel !== null && <span className="text-xs font-normal">{servedLabel}</span>}
    </Button>
  );
}
